
import { useState, useEffect } from "react";
import { useUppdragPage } from "./hooks/useUppdragPage";
import { useAuth } from "@/context/auth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import InitialLoader from "./components/InitialLoader";
import MainContent from "./components/MainContent";
import CreateFolderDialogWrapper from "./components/CreateFolderDialogWrapper";
import { useProjectHandlers } from "./components/ProjectHandlers";
import { useFolderHandlers } from "./components/FolderHandlers";
import { useUppdragPageEffects } from "./hooks/useUppdragPageEffects";
import { createUppdragUtils } from "./utils/uppdragUtils";
import { parseRouteParams, parseSearchParams, useCustomRouteChange } from "@/utils/customRouter";
import { useNavigate } from "react-router-dom";

const UppdragPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const routeParams = parseRouteParams();
  const searchParams = parseSearchParams();
  const routeProjectId = routeParams.projektId;
  const routeFolderId = routeParams.folderId;
  const acceptShareId = searchParams.accept_share;
  const [isCreateFolderOpen, setIsCreateFolderOpen] = useState(false);
  const [folderName, setFolderName] = useState("");

  const {
    selectedFolderId,
    projectId,
    projectName,
    isLoading,
    initialLoadComplete,
    activeTab,
    activeSubTab,
    isSharedFolder,
    fetchError,
    hasProjects,
    folderTreeKey,
    setActiveTab,
    setActiveSubTab,
    handleSelectProject,
    handleSelectSharedFolder,
    handleCreateRootFolder,
    handleStartAssessment,
    handleBackToFolders,
    handleSelectFolder,
    handleNavigateToAssessment,
    handleCreateNewAssessment,
    fetchDefaultProject,
    fetchProjectIdForSharedFolder,
    refreshFolderTree
  } = useUppdragPage();

  // Initialize handlers and utilities
  const { handleCreateProject } = useProjectHandlers({
    user,
    onProjectCreated: handleSelectProject
  });

  const { handleCreateFolderSubmit } = useFolderHandlers({
    user,
    projectId,
    onFolderCreated: () => {
      setIsCreateFolderOpen(false);
      setFolderName("");
      refreshFolderTree();
    }
  });

  const { handleProjectChange, handleRetry } = createUppdragUtils({
    handleSelectProject,
    refreshFolderTree,
    fetchDefaultProject
  });

  // Handle folder creation dialog submission
  const handleFolderDialogSubmit = async () => {
    const success = await handleCreateFolderSubmit(folderName);
    if (success) {
      // Dialog is closed by the handler
    }
  };

  // Handle project creation with selection
  const handleCreateProjectWithSelection = async (name: string) => {
    await handleCreateProject(name);
  };

  // Handle when a new project is created (including sample project)
  const handleProjectCreated = async (projectId: string) => {
    await handleSelectProject(projectId);
  };

  // Handle share acceptance from email link
  useEffect(() => {
    const handleShareAcceptance = async () => {
      if (acceptShareId && user) {
        try {
          console.log("Attempting to accept share:", acceptShareId);
          
          // First, check if share exists and is for this user
          const { data: shareData, error: shareError } = await supabase
            .from("project_shares")
            .select("*, projects(name)")
            .eq("id", acceptShareId)
            .eq("shared_with_email", user.email?.toLowerCase())
            .eq("accepted", false)
            .maybeSingle();

          if (shareError || !shareData) {
            console.error("Share not found or error:", shareError);
            toast.error("Delningen kunde inte hittas eller är redan accepterad");
            navigate("/uppdrag", { replace: true });
            return;
          }

          // Accept the share
          const { error: updateError } = await supabase
            .from("project_shares")
            .update({ 
              accepted: true, 
              shared_with_id: user.id,
              accepted_at: new Date().toISOString()
            })
            .eq("id", acceptShareId);

          if (updateError) {
            console.error("Error accepting share:", updateError);
            toast.error("Kunde inte acceptera delningen");
            return;
          }

          toast.success(`Projektet "${shareData.projects?.name || 'Projekt'}" har accepterats!`);
          
          // Navigate to the shared projects tab
          navigate("/uppdrag", { replace: true });
          setActiveTab("projects");
          setActiveSubTab("shared");
          
        } catch (error) {
          console.error("Error in share acceptance:", error);
          toast.error("Ett fel uppstod vid acceptering av delningen");
        }
      }
    };

    handleShareAcceptance();
  }, [acceptShareId, user, setActiveTab, setActiveSubTab]);

  // Listen for browser navigation (back/forward buttons)
  useCustomRouteChange(() => {
    // Force re-render when URL changes via browser navigation
    const newParams = parseRouteParams();
    if (newParams.projektId && newParams.projektId !== projectId) {
      handleSelectProject(newParams.projektId);
    }
    if (newParams.folderId && newParams.folderId !== selectedFolderId) {
      handleSelectFolder(newParams.folderId);
    }
  });

  // Initialize effects
  useUppdragPageEffects({
    user,
    initialLoadComplete,
    routeProjectId,
    routeFolderId,
    selectedFolderId,
    projectId,
    fetchDefaultProject,
    handleSelectFolder,
    setActiveTab
  });

  // Show InitialLoader until initial load completes; also show on definitive "no projects" empty state
  const shouldShowInitialLoader =
    !initialLoadComplete ||
    (hasProjects === false && !selectedFolderId && initialLoadComplete && !projectId);
                                    
  if (shouldShowInitialLoader) {
    return (
      <InitialLoader
        initialLoadComplete={initialLoadComplete}
        isLoading={isLoading}
        fetchError={fetchError}
        hasProjects={hasProjects}
        selectedFolderId={selectedFolderId}
        onRetry={handleRetry}
        onCreateProject={handleCreateProjectWithSelection}
        onProjectCreated={handleProjectCreated}
        onProjectSelect={handleSelectProject}
        onFolderSelect={handleSelectSharedFolder}
      />
    );
  }

  return (
    <>
      <MainContent
        projectId={projectId}
        projectName={projectName}
        selectedFolderId={selectedFolderId}
        activeTab={activeTab}
        activeSubTab={activeSubTab}
        isSharedFolder={isSharedFolder}
        folderTreeKey={folderTreeKey}
        isLoading={isLoading}
        fetchError={fetchError}
        user={user}
        setActiveTab={setActiveTab}
        setActiveSubTab={setActiveSubTab}
        handleCreateFolder={() => setIsCreateFolderOpen(true)}
        handleProjectChange={handleProjectChange}
        handleCreateProject={handleCreateProject}
        handleSelectProject={handleSelectProject}
        handleSelectSharedFolder={handleSelectSharedFolder}
        handleCreateRootFolder={handleCreateRootFolder}
        handleStartAssessment={handleStartAssessment}
        handleBackToFolders={handleBackToFolders}
        handleSelectFolder={handleSelectFolder}
        handleNavigateToAssessment={handleNavigateToAssessment}
        handleCreateNewAssessment={handleCreateNewAssessment}
        fetchProjectIdForSharedFolder={fetchProjectIdForSharedFolder}
        refreshFolderTree={refreshFolderTree}
      />

      <CreateFolderDialogWrapper
        isCreateFolderOpen={isCreateFolderOpen}
        setIsCreateFolderOpen={setIsCreateFolderOpen}
        folderName={folderName}
        setFolderName={setFolderName}
        onSubmit={handleFolderDialogSubmit}
      />
    </>
  );
};

export default UppdragPage;
